import { useState } from 'react'
import { useIntl } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircle, faPlus } from '@fortawesome/free-solid-svg-icons'
import { faClock } from '@fortawesome/free-regular-svg-icons'
import classNames from 'classnames'
import { TextSpinner } from 'ui'
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from 'ui/Tooltip'
import { toast } from 'react-toastify'
import Card from '../../common/Card'
import { useGetPartnerNotes } from '../../../hooks/useGetPartnerNotes/useGetPartnerNotes'
import { useCreatePartnerNote } from '../../../hooks/useCreatePartnerNote/useCreatePartnerNote'
import { temp_createEventDateFromISO } from 'utils/dates'
import { useAppSelector } from '../../../store/hooks'

type Props = {
   memberId: string
}

const Notes = ({ memberId }: Props) => {
   const intl = useIntl()
   const { locale } = useAppSelector((state) => state.base)
   const [showAll, setShowAll] = useState(false)
   const [isAdding, setIsAdding] = useState(false)
   const [noteContent, setNoteContent] = useState('')

   const { data: notes, status } = useGetPartnerNotes(memberId)
   const { mutate: createNote, isPending: isCreating } = useCreatePartnerNote()

   const displayedNotes = showAll ? notes : notes?.slice(0, 3)

   function handleSave() {
      if (!noteContent.trim()) return
      createNote(
         { memberId, content: noteContent.trim() },
         {
            onSuccess: () => {
               toast.success(intl.formatMessage({ id: 'partner.notes.create.success' }))
               setNoteContent('')
               setIsAdding(false)
            },
            onError: () => {
               toast.error(intl.formatMessage({ id: 'partner.notes.create.fail' }))
            },
         },
      )
   }

   function handleCancel() {
      setNoteContent('')
      setIsAdding(false)
   }

   return (
      <Card>
         <Card.Title>
            <div className="flex justify-between items-center">
               {intl.formatMessage({ id: 'partner.notes' })}
               {!isAdding && (
                  <button
                     onClick={() => setIsAdding(true)}
                     className="text-sm text-neutral-500 hover:text-neutral-700 font-normal"
                  >
                     <FontAwesomeIcon icon={faPlus} size="xs" className="mr-1" />
                     {intl.formatMessage({ id: 'partner.notes.add' })}
                  </button>
               )}
            </div>
         </Card.Title>

         {isAdding && (
            <div className="p-4 border-b">
               <textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'partner.notes.placeholder' })}
                  className="w-full h-24 border border-gray-300 rounded p-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
               />
               <div className="flex gap-x-2 mt-2">
                  <button
                     onClick={handleSave}
                     disabled={isCreating || !noteContent.trim()}
                     className="bg-amber-500 text-white text-sm px-4 py-1.5 rounded disabled:opacity-50"
                  >
                     {intl.formatMessage({ id: 'partner.notes.save' })}
                  </button>
                  <button
                     onClick={handleCancel}
                     disabled={isCreating}
                     className="border border-gray-300 text-sm px-4 py-1.5 rounded hover:bg-gray-50"
                  >
                     {intl.formatMessage({ id: 'partner.notes.cancel' })}
                  </button>
               </div>
            </div>
         )}

         {status === 'pending' ? (
            Array.from({ length: 2 }, (_, i) => (
               <div className="p-4" key={i}>
                  <div className="flex gap-2">
                     <FontAwesomeIcon icon={faCircle} size="xs" className="mt-1.5 text-neutral-400" />
                     <div className="w-full">
                        <TextSpinner className="mb-2" />
                        <TextSpinner />
                     </div>
                  </div>
               </div>
            ))
         ) : status === 'success' && (!notes || notes.length === 0) ? (
            <div className="p-4 text-center text-neutral-400 text-sm">
               {intl.formatMessage({ id: 'partner.notes.empty' })}
            </div>
         ) : (
            <>
               <div className="max-h-96 overflow-y-auto">
                  {displayedNotes?.map((note) => (
                     <div className="p-4" key={note.id}>
                        <div className="flex gap-2">
                           <FontAwesomeIcon icon={faCircle} size="xs" className="mt-1.5 text-green-500" />
                           <div>
                              <p className="text-sm">{note.content}</p>
                              <p className="text-neutral-400 text-xs mt-1">
                                 {intl.formatMessage({ id: 'partner.notes.by' }, { author: note.authorName })}
                              </p>
                              <p className="text-neutral-400 text-xs mt-0.5">
                                 {(() => {
                                    try {
                                       const convertedDate = temp_createEventDateFromISO(note.createdAt).setLocale(locale)
                                       return (
                                          <TooltipProvider delayDuration={0}>
                                             <Tooltip>
                                                <TooltipTrigger className="flex items-center gap-1">
                                                   <FontAwesomeIcon icon={faClock} size="xs" />
                                                   {convertedDate.toRelative()}
                                                </TooltipTrigger>
                                                <TooltipContent className="w-48 text-center">
                                                   <TooltipArrow />
                                                   {convertedDate.toLocaleString({
                                                      weekday: 'long',
                                                      year: 'numeric',
                                                      month: 'long',
                                                      day: '2-digit',
                                                      hour: '2-digit',
                                                      minute: '2-digit',
                                                   })}
                                                </TooltipContent>
                                             </Tooltip>
                                          </TooltipProvider>
                                       )
                                    } catch {
                                       return note.createdAt
                                    }
                                 })()}
                              </p>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>

               {notes && notes.length > 1 && (
                  <div className="border-t px-4 py-3">
                     <button
                        onClick={() => setShowAll(!showAll)}
                        className={classNames('text-sm text-blue-500 hover:underline')}
                     >
                        {showAll
                           ? intl.formatMessage({ id: 'partner.notes.view.latest' })
                           : intl.formatMessage({ id: 'partner.notes.view.all' })}
                     </button>
                  </div>
               )}
            </>
         )}
      </Card>
   )
}

export default Notes
